import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import NpvrSlider, { NpvrSliderItem } from './npvr-slider';
import ExtendIcon from '../icons/extend';
import extendRecording from '@smart-tv/utils/kmm/extendRecording';
import { rootStore } from './_stores/store-root';
import { NpvrResourceKey } from './_stores/store-npvr';
import { NpvrEventItem } from '@smart-tv/typings/npvr';
import { mapNpvrEventToCardProps } from '@smart-tv/utils/kmm/helpers';
import { getNPVRCardFocusKey } from './npvr-helpers';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { IconTypes, useToast } from '@smart-tv/hooks/use-toast/use-toast';
import useDelayedSpinner from '@smart-tv/hooks/use-delayed-spinner/use-delayed-spinner';
import NpvrLoading from './npvr-loading';

export const NpvrExpiring: React.FC = observer(() => {
  const { data: expiringSoonEvents } = rootStore.npvrStore.expiringSoon;
  const [overlays, setOverlays] = React.useState<Record<string, Partial<NpvrSliderItem>>>({});
  const { showToast } = useToast();
  const showSpinner = useDelayedSpinner(expiringSoonEvents === undefined);

  const updateItem = React.useCallback((id: string, patch: Partial<NpvrSliderItem>) => {
    setOverlays((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  const add90DaysExpiry = (expiryTime: number | null, extendDurationInSec: number | null) => {
    if (!expiryTime || !extendDurationInSec) return undefined;
    console.log('Original expiry time:', expiryTime);
    const date = new Date((expiryTime + extendDurationInSec) * 1000);
    const currentDate = new Date();
    const differenceInSeconds = (date.getTime() - currentDate.getTime()) / 1000;
    const number = Math.floor(differenceInSeconds / (60 * 60 * 24));
    return number + 'dage';
  };

  const handleFocus = React.useCallback((id: string) => {
    const indexOfEvent = rootStore.npvrStore.expiringSoon.data?.findIndex(
      (event) => String(event.eventId) === id
    );
    setFocus(getNPVRCardFocusKey(indexOfEvent ?? 0));
  }, []);

  const handleExtend = async (event: NpvrEventItem) => {
    const id = String(event.eventId);
    const action = { icon: <ExtendIcon />, label: 'Forlæng' };
    const retryAction = { ...action, onClick: () => handleExtend(event) };

    updateItem(id, { action: { ...action, loading: true } });
    try {
      const extended = await extendRecording(event.sid, event.eventId);
      updateItem(
        id,
        extended
          ? {
              successMessage: 'Forlænget i 3 måneder',
              expiryLabel: add90DaysExpiry(
                event.recordingExpireAtUtcSeconds,
                event.npvrExtendDurationInSec
              )
            }
          : { action: retryAction }
      );
      if (extended) {
        handleFocus(id);
        // recordings is included too - Optaget's cards read the same event's
        // readableExpireTime, which this extension also changes.
        rootStore.npvrStore.invalidate([
          NpvrResourceKey.ExpiringSoon,
          NpvrResourceKey.Recordings,
          NpvrResourceKey.Status
        ]);
        showToast('Optagelsen blev forlænget', IconTypes.success);
      } else {
        showToast('Fejl, prøv igen senere', IconTypes.failure);
      }
    } catch (error) {
      showToast('Fejl, prøv igen senere', IconTypes.failure);
      updateItem(id, { action: retryAction });
    }
  };

  useEffect(() => {
    rootStore.npvrStore.fetchExpiringSoon().catch((error) => console.error(error));
  }, []);

  if (showSpinner) return <NpvrLoading />;
  if (expiringSoonEvents === undefined) return null;

  const sliderItems: NpvrSliderItem[] = expiringSoonEvents.map((event) => {
    const id = String(event.eventId);
    return {
      ...mapNpvrEventToCardProps(event, true),
      action: event.npvrInfo?.isNpvrExtendAllowed
        ? { icon: <ExtendIcon />, label: 'Forlæng', onClick: () => handleExtend(event) }
        : { icon: <ExtendIcon />, label: 'Kan ikke forlænges', disabled: true },
      ...overlays[id]
    };
  });

  const displayString =
    expiringSoonEvents.length === 0
      ? 'Du har ingen optagelser, som der er ved at udløbe....'
      : 'Nogle af dine optagelser udløber snart. Klik på forlæng optagelse for at forlænge med tre måneder.';
  return (
    <>
      <NpvrSlider items={sliderItems} displayString={displayString} />
    </>
  );
});

export default NpvrExpiring;

